// The keyboard's way through the feed (dioxus-plan.md D382).
//
// ArrowUp from the very top of the composer's box takes the focus to the
// newest message drawn; on a message the arrows step to its neighbours,
// ArrowDown off the newest one and Escape go back to the box, and E edits it.
// Which key does what — and whether a message may be edited at all — is each
// client's own handler's to decide, beside the composer's Enter and Escape.
// What is here is the part that is the same DOM in both clients (D57): which
// element is «the message above», how it comes to take the focus, and how far
// the column moves to show it.
//
//     window.__echoFeedKeys.last()   // focus the newest message; false if none
//     window.__echoFeedKeys.step(d)  // -1 older, +1 newer, from the focused one
//     window.__echoFeedKeys.box()    // back to the composer's box
//
// A message can take the focus only while it holds it. A `tabindex` on every
// bubble would let a click focus one as well, and a clicked message would then
// take the arrows that scroll the feed today and step through messages with
// them instead. So the attribute goes on as the walk arrives and comes off as
// the focus leaves, and the pointer, Tab and the arrows after a click do what
// they always did. Not when it is the whole window that loses the focus: the
// message stays the document's active element through that, and an attribute
// taken off then would let the engine's focus fixup drop it on <body> before
// the reader is back.
//
// The column is moved by hand rather than by `scrollIntoView`. In the wide
// layout the header and the composer float over the feed and the scroller pays
// them back as padding (`.chat-feed__scroll` in app.css), so a message parked
// under the composer is «in view» to the engine and behind glass to the
// reader. The band a message has to stand in is the scroller's box less its
// padding.
//
// Moving the column *up* is the reader's own hand, like a wheel turned upwards
// (`handoff_script` in the desktop's message.rs, `takeOver` in the web's
// feed.tsx): the follow of an arrival that is still settling would otherwise
// pull them back down to the bottom. So before such a move the scroller hears
// `echo-take`, which each client answers the way it answers that wheel.
//
// Shared by both clients for the reason `ink.js` is: the desktop inlines it
// into the head of its window, the web imports it at the entry.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoFeedKeys) return;

  var FEED = "feed-scroll";
  var MESSAGE = ".message-bubble";
  var BOX = ".composer__input";

  function messages(scroller) {
    return scroller ? Array.prototype.slice.call(scroller.querySelectorAll(MESSAGE)) : [];
  }

  // Off again once the focus has gone somewhere else in the page.
  function release(e) {
    if (!document.hasFocus()) return;
    e.currentTarget.removeEventListener("blur", release);
    e.currentTarget.removeAttribute("tabindex");
  }

  function take(scroller) {
    scroller.dispatchEvent(new Event("echo-take"));
  }

  // The least move that puts el inside the band; a message taller than the
  // band shows its top, where reading it starts.
  function show(scroller, el) {
    var style = getComputedStyle(scroller);
    var box = scroller.getBoundingClientRect();
    var top = box.top + (parseFloat(style.paddingTop) || 0);
    var bottom = box.bottom - (parseFloat(style.paddingBottom) || 0);
    var rect = el.getBoundingClientRect();
    var by = 0;
    if (rect.top < top || rect.height > bottom - top) by = rect.top - top;
    else if (rect.bottom > bottom) by = rect.bottom - bottom;
    if (by < 0) take(scroller);
    if (by) scroller.scrollTop += by;
  }

  function land(el) {
    if (!el.hasAttribute("tabindex")) {
      el.setAttribute("tabindex", "-1");
      el.addEventListener("blur", release);
    }
    el.focus({ preventScroll: true });
    var scroller = document.getElementById(FEED);
    if (scroller) show(scroller, el);
    return true;
  }

  function box() {
    var el = document.querySelector(BOX);
    if (!el) return false;
    el.focus();
    return true;
  }

  function last() {
    var all = messages(document.getElementById(FEED));
    return all.length > 0 && land(all[all.length - 1]);
  }

  function step(d) {
    var scroller = document.getElementById(FEED);
    var all = messages(scroller);
    var focused = document.activeElement;
    var here = focused && focused.closest ? focused.closest(MESSAGE) : null;
    var at = here ? all.indexOf(here) : -1;
    if (at < 0) return false;
    var next = all[at + d];
    if (next) return land(next);
    // Past the newest: back where the walk came in.
    if (d > 0) return box();
    // Past the oldest drawn: the column goes to its top, and the scroll is
    // what asks for more above — the next press finds it there.
    if (scroller.scrollTop > 0) {
      take(scroller);
      scroller.scrollTop = 0;
    }
    return false;
  }

  window.__echoFeedKeys = { last: last, step: step, box: box };
})();
